import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faStore, 
  faUser, 
  faPhone, 
  faEnvelope, 
  faMoneyBillWave,
  faCheckCircle,
  faTimesCircle,
  faArrowLeft,
  faSearch,
  faCalendarAlt,
  faMapMarkerAlt,
  faBuilding,
  faCreditCard,
  faCashRegister,
  faSave,
  faInfoCircle,
  faSms,
  faPercent,
  faTag,
  faImage,
  faUpload,
  faTrash,
  faUserPlus,
  faIdCard
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import "./AdminStallBooking.css";

// API Base URL - LOCAL (comment out for production)
//const API_BASE_URL = 'http://localhost/royaleheritagefiesta/api';
const API_BASE_URL = 'https://royaleheritagefiesta.com/royaleheritagefiesta/api'; // UNCOMMENT FOR PRODUCTION

function AdminStallBooking() {
  const location = useLocation();
  const navigate = useNavigate();
  const eventId = location.state?.eventId;

  // ========== STATE MANAGEMENT ==========
  const [event, setEvent] = useState(null);
  const [stalls, setStalls] = useState([]);
  const [customStalls, setCustomStalls] = useState([]);
  const [isCustomLayout, setIsCustomLayout] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Stats
  const [stats, setStats] = useState({
    totalStalls: 0,
    bookedCount: 0,
    availableCount: 0
  });

  // UI States
  const [selectedStall, setSelectedStall] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [hoveredStall, setHoveredStall] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  // Customer Form State
  const [customer, setCustomer] = useState({
    name: "",
    phone: "",
    email: "",
    company: "",
    notes: ""
  });

  // NEW: Booked By State (Admin/Staff details)
  const [bookedBy, setBookedBy] = useState({
    name: "",
    phone: ""
  });

  // Payment State
  const [paymentMethod, setPaymentMethod] = useState("CASH");
  const [paymentStatus, setPaymentStatus] = useState("PAID");
  const [originalPrice, setOriginalPrice] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [finalPrice, setFinalPrice] = useState(0);
  const [paymentReceipt, setPaymentReceipt] = useState(null);
  const [paymentReceiptPreview, setPaymentReceiptPreview] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [whatsappSending, setWhatsappSending] = useState(false);
  const [whatsappSent, setWhatsappSent] = useState(false);
  const [uploadedReceiptPath, setUploadedReceiptPath] = useState(null);
  const fileInputRef = useRef(null);

  // Layout display states
  const [stallPattern, setStallPattern] = useState("full");
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 800, height: 1100 });
  const [gridSize, setGridSize] = useState(40);
  const [showBlueprint, setShowBlueprint] = useState(true);
  const [imageOpacity] = useState(0.8);
  const [fixedElements, setFixedElements] = useState([]);

  // Zoom state for mobile
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showFloatingButtons, setShowFloatingButtons] = useState(false);

  // Company details for WhatsApp
  const COMPANY_WHATSAPP = "919391802187";
  const COMPANY_EMAIL = "royaleheritagefiesta@gmail.com";
  const COMPANY_NAME = "Royal Heritage Fiesta";

  // Scroll listener for floating buttons
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
      setShowFloatingButtons(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleGlobalWhatsAppClick = () => {
    window.open(`https://wa.me/${COMPANY_WHATSAPP}?text=Hi%20Royal%20Heritage%20Fiesta%20team,%20I%27m%20interested%20in%20booking%20a%20stall.`, '_blank');
  };

  // Zoom functions
  const handleZoomIn = () => {
    if (zoomLevel < 2) {
      setZoomLevel(prev => Math.min(2, prev + 0.1));
    }
  };

  const handleZoomOut = () => {
    if (zoomLevel > 0.5) {
      setZoomLevel(prev => Math.max(0.5, prev - 0.1));
    }
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
  };

  // Update final price when discount changes
  const handleDiscountChange = (e) => {
    const discountAmount = parseFloat(e.target.value) || 0;
    setDiscount(discountAmount);
    const newFinalPrice = Math.max(0, originalPrice - discountAmount);
    setFinalPrice(newFinalPrice);
  };

  // Handle payment receipt upload
  const handlePaymentReceiptUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("File size should be less than 5MB");
        return;
      }
      
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alert("Please upload only JPG, PNG, or WEBP images");
        return;
      }
      
      setPaymentReceipt(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPaymentReceiptPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePaymentReceipt = () => {
    setPaymentReceipt(null);
    setPaymentReceiptPreview(null);
    setUploadedReceiptPath(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Helper function to get total stalls by pattern
  const getTotalStallsByPattern = (pattern) => {
    switch(pattern) {
      case "full": return 50;
      case "ushape": return 50;
      case "twosides": return 50;
      default: return 50;
    }
  };

  // ========== SEND WHATSAPP CONFIRMATION with Clickable Receipt Link ==========
  const sendWhatsAppConfirmation = async (customerData, stallData, eventData, discountAmount, finalAmount, receiptPath, bookingId) => {
    setWhatsappSending(true);
    
    try {
      const discountText = discountAmount > 0 ? `\n• Discount: *-₹${discountAmount}*` : '';
      
      // Create a proper clickable link with booking ID
      let receiptText = '';
      if (receiptPath) {
        const receiptUrl = `${API_BASE_URL.replace('/api', '')}/receipt-viewer.php?file=${receiptPath.split('/').pop()}&booking_id=${bookingId}`;
        receiptText = `\n\n📸 *Payment Receipt:*\n${receiptUrl}`;
      }
      
      const message = `*${COMPANY_NAME} - Stall Booking Confirmation* 🎉

Dear *${customerData.name}*,

Your stall has been successfully booked for *${eventData.eventName}*!

━━━━━━━━━━━━━━━━━━━━
📋 *Booking Details:*
━━━━━━━━━━━━━━━━━━━━
• Payment ID: *#${String(bookingId).padStart(6, '0')}*
• Stall Number: *${stallData.stallId || stallData.number}*
• Event: ${eventData.eventName}
• Date: ${new Date(eventData.eventDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
• Venue: ${eventData.place || 'Venue TBA'}

━━━━━━━━━━━━━━━━━━━━
💰 *Payment Details:*
━━━━━━━━━━━━━━━━━━━━
• Original Price: *₹${stallData.price}*
${discountText}
• Final Price: *₹${finalAmount}*
• Payment Method: ${paymentMethod}
• Payment Status: ${paymentStatus}
${receiptText}

━━━━━━━━━━━━━━━━━━━━
📞 *Need Help?*
━━━━━━━━━━━━━━━━━━━━
• WhatsApp: +91 ${COMPANY_WHATSAPP}
• Email: ${COMPANY_EMAIL}

Thank you for choosing ${COMPANY_NAME}! 🤝

*This is an automated confirmation. Please save this message for reference.*`;

      const encodedMessage = encodeURIComponent(message);
      const cleanPhone = customerData.phone.replace(/\D/g, '');
      const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
      
      window.open(whatsappUrl, '_blank');
      setWhatsappSent(true);
      return true;
    } catch (error) {
      console.error("WhatsApp sending failed:", error);
      return false;
    } finally {
      setWhatsappSending(false);
    }
  };

  // ========== FETCH EVENT DATA ==========
  useEffect(() => {
    if (!eventId) {
      setError("No event selected!");
      setLoading(false);
      return;
    }
    
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const eventRes = await axios.get(`${API_BASE_URL}/events.php?eventId=${eventId}`);
        console.log("Event details:", eventRes.data);
        
        setEvent(eventRes.data);
        setStallPattern(eventRes.data.stallPattern || "full");
        
        const hasCustomLayout = eventRes.data.isCustomLayout === true || eventRes.data.stallPattern === "custom";
        setIsCustomLayout(hasCustomLayout);
        
        let bookedStallsMap = {};
        try {
          const bookingsRes = await axios.get(`${API_BASE_URL}/stall-bookings.php?eventId=${eventId}`);
          console.log("Bookings response:", bookingsRes.data);
          
          if (bookingsRes.data.success && bookingsRes.data.bookings) {
            bookingsRes.data.bookings.forEach(booking => {
              bookedStallsMap[booking.stallNumber] = {
                customerName: booking.customerName,
                customerPhone: booking.customerPhone,
                customerEmail: booking.customerEmail,
                bookingDate: booking.bookingDate,
                bookedBy: booking.bookedBy,
                bookedByPhone: booking.bookedByPhone
              };
            });
          }
        } catch (bookingError) {
          console.log("No bookings found or error:", bookingError);
        }
        
        if (hasCustomLayout && eventRes.data.customLayoutData) {
          const layoutData = typeof eventRes.data.customLayoutData === 'string' 
            ? JSON.parse(eventRes.data.customLayoutData) 
            : eventRes.data.customLayoutData;
          
          const stallsWithStatus = (layoutData.stalls || []).map(stall => {
            const bookedInfo = bookedStallsMap[stall.number];
            const isBooked = !!bookedInfo;
            
            return {
              ...stall,
              stallId: stall.number,
              booked: isBooked,
              customerName: bookedInfo?.customerName,
              customerPhone: bookedInfo?.customerPhone,
              customerEmail: bookedInfo?.customerEmail,
              bookingDate: bookedInfo?.bookingDate,
              bookedBy: bookedInfo?.bookedBy,
              bookedByPhone: bookedInfo?.bookedByPhone
            };
          });
          
          setCustomStalls(stallsWithStatus);
          setFixedElements(layoutData.fixedElements || []);
          setBackgroundImage(layoutData.backgroundImage || eventRes.data.blueprintImage);
          setImageDimensions(layoutData.imageDimensions || { width: 800, height: 1100 });
          setGridSize(layoutData.gridSize || 40);
          
          const bookedCount = stallsWithStatus.filter(s => s.booked).length;
          setStats({
            totalStalls: stallsWithStatus.length,
            bookedCount: bookedCount,
            availableCount: stallsWithStatus.length - bookedCount
          });
          
        } else {
          const totalStallsCount = getTotalStallsByPattern(stallPattern);
          const generatedStalls = [];
          
          for (let i = 1; i <= totalStallsCount; i++) {
            const bookedInfo = bookedStallsMap[i];
            generatedStalls.push({
              stallId: i,
              price: eventRes.data.stallPrice || 8000,
              booked: !!bookedStallsMap[i],
              bookedBy: bookedInfo?.bookedBy,
              bookedByPhone: bookedInfo?.bookedByPhone
            });
          }
          
          setStalls(generatedStalls);
          
          const bookedCount = generatedStalls.filter(s => s.booked).length;
          setStats({
            totalStalls: totalStallsCount,
            bookedCount: bookedCount,
            availableCount: totalStallsCount - bookedCount
          });
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load event data");
        setLoading(false);
      }
    };

    fetchData();
  }, [eventId, stallPattern]);

  // ========== FILTER STALLS ==========
  const getFilteredStalls = () => {
    let filtered = isCustomLayout ? [...customStalls] : [...stalls];
    
    if (filterStatus === "available") {
      filtered = filtered.filter(s => !s.booked);
    } else if (filterStatus === "booked") {
      filtered = filtered.filter(s => s.booked);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(s => 
        (s.stallId || s.number || '').toString().includes(searchTerm)
      );
    }
    
    return filtered;
  };

  // ========== HANDLE STALL CLICK ==========
  const handleStallClick = (stall) => {
    if (stall.booked) {
      alert(`Stall #${stall.stallId || stall.number} is already booked.`);
      return;
    }
    setSelectedStall(stall);
    setOriginalPrice(stall.price);
    setFinalPrice(stall.price);
    setDiscount(0);
    setPaymentReceipt(null);
    setPaymentReceiptPreview(null);
    setUploadedReceiptPath(null);
    setBookedBy({ name: "", phone: "" }); // Reset booked by
    setShowBookingForm(true);
    setWhatsappSent(false);
  };

  const handleCustomStallClick = (stall) => {
    if (stall.booked) {
      alert(`Stall #${stall.number} is already booked.`);
      return;
    }
    setSelectedStall({
      stallId: stall.number,
      price: stall.price,
      ...stall
    });
    setOriginalPrice(stall.price);
    setFinalPrice(stall.price);
    setDiscount(0);
    setPaymentReceipt(null);
    setPaymentReceiptPreview(null);
    setUploadedReceiptPath(null);
    setBookedBy({ name: "", phone: "" }); // Reset booked by
    setShowBookingForm(true);
    setWhatsappSent(false);
  };

  // ========== RESET FORM ==========
  const resetForm = () => {
    setCustomer({
      name: "",
      phone: "",
      email: "",
      company: "",
      notes: ""
    });
    setBookedBy({ name: "", phone: "" }); // Reset booked by
    setPaymentMethod("CASH");
    setPaymentStatus("PAID");
    setDiscount(0);
    setFinalPrice(originalPrice);
    setPaymentReceipt(null);
    setPaymentReceiptPreview(null);
    setUploadedReceiptPath(null);
    setSelectedStall(null);
    setShowBookingForm(false);
    setWhatsappSent(false);
  };

  // ========== HANDLE BOOKING SUBMIT ==========
  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    if (!customer.name || !customer.phone) {
      alert("Please fill all required fields");
      return;
    }
    
    if (customer.phone.length !== 10) {
      alert("Please enter a valid 10-digit phone number");
      return;
    }
    
    if (!bookedBy.name) {
      alert("Please enter your name (Booked By)");
      return;
    }
    
    if (!bookedBy.phone || bookedBy.phone.length !== 10) {
      alert("Please enter a valid 10-digit phone number for Booked By");
      return;
    }
    
    setIsSaving(true);
    
    try {
      let receiptPath = null;
      
      if (paymentReceipt) {
        const formData = new FormData();
        formData.append('payment_receipt', paymentReceipt);
        formData.append('eventId', eventId);
        formData.append('stallNumber', selectedStall.stallId || selectedStall.number);
        
        const uploadResponse = await axios.post(`${API_BASE_URL}/upload-receipt.php`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (uploadResponse.data.success) {
          receiptPath = uploadResponse.data.filepath;
          setUploadedReceiptPath(receiptPath);
        }
      }
      
      const bookingData = {
        eventId: parseInt(eventId),
        stallNumber: selectedStall.stallId || selectedStall.number,
        stallPrice: originalPrice,
        discount: discount,
        finalPrice: finalPrice,
        paymentReceipt: receiptPath,
        customerName: customer.name,
        customerPhone: customer.phone,
        customerEmail: customer.email || "",
        customerCompany: customer.company || "",
        customerRequirements: customer.notes || "",
        bookedBy: bookedBy.name,  // NEW: Booked by name
        bookedByPhone: bookedBy.phone,  // NEW: Booked by phone
        paymentMethod: paymentMethod,
        paymentStatus: paymentStatus,
        bookingStatus: "CONFIRMED"
      };
      
      console.log("Sending booking to PHP API:", bookingData);
      
      const bookingResponse = await axios.post(`${API_BASE_URL}/stall-bookings.php`, bookingData);
      
      console.log("Booking response:", bookingResponse.data);
      
      if (bookingResponse.data.success) {
        const newBookingId = bookingResponse.data.bookingId;
        
        if (isCustomLayout) {
          const updatedStalls = customStalls.map(s => 
            (s.number === selectedStall.number || s.stallId === selectedStall.stallId)
              ? { 
                  ...s, 
                  booked: true,
                  status: 'BOOKED',
                  bookedBy: customer.name,
                  customerName: customer.name,
                  customerPhone: customer.phone,
                  customerEmail: customer.email,
                  bookingDate: new Date().toISOString(),
                  bookedByName: bookedBy.name,
                  bookedByPhone: bookedBy.phone
                } 
              : s
          );
          setCustomStalls(updatedStalls);
        } else {
          const updatedStalls = stalls.map(s => 
            s.stallId === selectedStall.stallId ? { ...s, booked: true, bookedBy: bookedBy.name, bookedByPhone: bookedBy.phone } : s
          );
          setStalls(updatedStalls);
        }
        
        // Send WhatsApp confirmation with receipt link and booking ID
        await sendWhatsAppConfirmation(customer, selectedStall, event, discount, finalPrice, receiptPath, newBookingId);
        
        setSuccessMessage(`✅ Stall #${selectedStall.stallId || selectedStall.number} booked successfully! Booking ID: #${String(newBookingId).padStart(6, '0')}. WhatsApp confirmation sent to ${customer.phone}`);
        setShowSuccess(true);
        
        setStats(prev => ({
          ...prev,
          bookedCount: prev.bookedCount + 1,
          availableCount: prev.availableCount - 1
        }));
        
        resetForm();
        setTimeout(() => setShowSuccess(false), 5000);
      } else {
        throw new Error(bookingResponse.data.message || "Booking failed");
      }
      
    } catch (error) {
      console.error("Booking error:", error);
      alert("Failed to book stall: " + (error.response?.data?.error || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  // ========== RENDER FUNCTIONS ==========
  const renderStallCard = (stall) => (
    <div 
      key={stall.stallId} 
      className={`admin-stall-item ${stall.booked ? 'booked' : 'available'}`}
      onClick={() => !stall.booked && handleStallClick(stall)}
    >
      <div className={`admin-stall-card ${stall.booked ? 'booked' : 'available'}`}>
        <div className="stall-number">#{stall.stallId}</div>
        <div className="stall-price">₹{stall.price}</div>
        {stall.booked ? (
          <div className="stall-status booked">
            <FontAwesomeIcon icon={faTimesCircle} /> BOOKED
          </div>
        ) : (
          <div className="stall-status available">
            <FontAwesomeIcon icon={faCheckCircle} /> AVAILABLE
          </div>
        )}
      </div>
    </div>
  );

  const renderCustomStall = (stall) => {
    const numberFontSize = Math.max(10, Math.min(20, (stall.width || 60) / 4));
    const priceFontSize = Math.max(8, Math.min(16, (stall.width || 60) / 5));
    const iconFontSize = Math.max(14, Math.min(24, (stall.width || 60) / 3));
    
    const stallNumber = stall.number || stall.stallNumber || '?';
    const stallPrice = stall.price || 8000;
    const stallIcon = stall.icon || '🛍️';
    const stallColor = stall.color || (stall.booked ? '#dc3545' : '#98D8C8');
    
    const x = parseFloat(stall.x) || 0;
    const y = parseFloat(stall.y) || 0;
    const width = parseFloat(stall.width) || 60;
    const height = parseFloat(stall.height) || 60;

    return (
      <div
        key={stall.id || `stall-${stallNumber}`}
        className={`admin-custom-stall ${stall.booked ? 'booked' : 'available'}`}
        onClick={() => !stall.booked && handleCustomStallClick(stall)}
        onMouseEnter={() => setHoveredStall(stall)}
        onMouseLeave={() => setHoveredStall(null)}
        style={{
          position: 'absolute',
          left: `${x}px`,
          top: `${y}px`,
          width: `${width}px`,
          height: `${height}px`,
          backgroundColor: stallColor,
          border: stall.booked ? '3px solid #dc3545' : '2px solid #28a745',
          borderRadius: '8px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: stall.textColor || 'black',
          cursor: stall.booked ? 'not-allowed' : 'pointer',
          boxShadow: stall.booked 
            ? '0 4px 8px rgba(220,53,69,0.3)' 
            : '0 4px 8px rgba(40,167,69,0.3)',
          transition: 'all 0.2s',
          zIndex: 20,
          padding: '2px',
          fontSize: '12px',
          fontWeight: 'bold',
          textAlign: 'center'
        }}
      >
        <div style={{ fontSize: `${numberFontSize}px`, fontWeight: 'bold' }}>
          #{stallNumber}
        </div>
        <div style={{ fontSize: `${priceFontSize}px` }}>
          ₹{stallPrice}
        </div>
        <div style={{ fontSize: `${iconFontSize}px` }}>
          {stallIcon}
        </div>
        {stall.booked && (
          <div className="booked-overlay">BOOKED</div>
        )}
      </div>
    );
  };

  const getStallsByPosition = () => {
    const sortedStalls = [...stalls].sort((a, b) => a.stallId - b.stallId);
    
    const positions = {
      top: [], bottom: [], left: [], right: [], leftInner: [], rightInner: []
    };

    switch(stallPattern) {
      case "full":
        positions.top = sortedStalls.filter(s => s.stallId >= 1 && s.stallId <= 12);
        positions.left = sortedStalls.filter(s => s.stallId >= 13 && s.stallId <= 24);
        positions.right = sortedStalls.filter(s => s.stallId >= 25 && s.stallId <= 36);
        positions.bottom = sortedStalls.filter(s => s.stallId >= 37 && s.stallId <= 50);
        break;
      case "ushape":
        positions.left = sortedStalls.filter(s => s.stallId >= 1 && s.stallId <= 12);
        positions.right = sortedStalls.filter(s => s.stallId >= 13 && s.stallId <= 24);
        positions.bottom = sortedStalls.filter(s => s.stallId >= 25 && s.stallId <= 38);
        positions.leftInner = sortedStalls.filter(s => s.stallId >= 39 && s.stallId <= 44);
        positions.rightInner = sortedStalls.filter(s => s.stallId >= 45 && s.stallId <= 50);
        break;
      case "twosides":
        positions.left = sortedStalls.filter(s => s.stallId >= 1 && s.stallId <= 25);
        positions.right = sortedStalls.filter(s => s.stallId >= 26 && s.stallId <= 50);
        break;
      default:
        positions.top = sortedStalls.filter(s => s.stallId >= 1 && s.stallId <= 12);
        positions.left = sortedStalls.filter(s => s.stallId >= 13 && s.stallId <= 24);
        positions.right = sortedStalls.filter(s => s.stallId >= 25 && s.stallId <= 36);
        positions.bottom = sortedStalls.filter(s => s.stallId >= 37 && s.stallId <= 50);
    }
    return positions;
  };

  const positionedStalls = getStallsByPosition();

  if (loading) {
    return (
      <>
        <div className="admin-loading-container">
          <div className="admin-loader"></div>
          <p>Loading event layout...</p>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className="admin-error-container">
          <div className="admin-error-card">
            <span className="error-icon">⚠️</span>
            <h3>Error Loading Event</h3>
            <p>{error}</p>
            <button className="back-btn" onClick={() => navigate('/adminDashboard')}>
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        .stat-value {
          color: #1e293b !important;
        }
        .stat-card.total .stat-value {
          color: #000 !important;
        }
        .stat-card.available .stat-value {
          color: #28a745 !important;
        }
        .stat-card.booked .stat-value {
          color: #dc3545 !important;
        }
        .stat-label {
          color: #64748b !important;
        }
      `}</style>

      <div className="admin-stall-booking">
        
        {/* Header */}
        <div className="admin-header">
          <div className="header-left">
            <button className="back-btn" onClick={() => navigate('/adminDashboard')}>
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <h1 style={{color:"black"}}>
              <FontAwesomeIcon icon={faStore} className="header-icon" />
              Admin Stall Booking
            </h1>
          </div>
          
          <div className="event-info">
            <span className="event-name">
              <FontAwesomeIcon icon={faBuilding} /> {event?.eventName}
            </span>
            <span className="event-date">
              <FontAwesomeIcon icon={faCalendarAlt} /> {new Date(event?.eventDate).toLocaleDateString()}
            </span>
            <span className="event-venue">
              <FontAwesomeIcon icon={faMapMarkerAlt} /> {event?.place || 'Venue TBA'}
            </span>
          </div>
        </div>

        {/* WhatsApp Info Banner */}
        <div className="whatsapp-info-banner">
          <FontAwesomeIcon icon={faWhatsapp} className="whatsapp-icon" />
          <span>WhatsApp confirmation will be sent to customer's number after successful booking from <strong>+91 {COMPANY_WHATSAPP}</strong></span>
        </div>

        {/* Stats Cards */}
        <div className="stats-cards">
          <div className="stat-card total">
            <div className="stat-icon">🏪</div>
            <div className="stat-content">
              <span className="stat-label">Total Stalls</span>
              <span className="" style={{color:"black",fontWeight:"700",}}>{stats.totalStalls}</span>
            </div>
          </div>
          <div className="stat-card available">
            <div className="stat-icon">🟢</div>
            <div className="stat-content">
              <span className="stat-label">Available</span>
              <span className=" available" style={{color:"green", fontWeight:"700"}}>{stats.availableCount}</span>
            </div>
          </div>
          <div className="stat-card booked">
            <div className="stat-icon">🔴</div>
            <div className="stat-content">
              <span className="stat-label">Booked</span>
              <span className=" booked" style={{color:"red", fontWeight:"700"}}>{stats.bookedCount}</span>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="search-filter-bar">
          <div className="search-box">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search stall number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="filter-buttons">
            <button 
              className={`filter-btn ${filterStatus === 'all' ? 'active' : ''}`}
              onClick={() => setFilterStatus('all')}
            >
              All ({stats.totalStalls})
            </button>
            <button 
              className={`filter-btn available ${filterStatus === 'available' ? 'active' : ''}`}
              onClick={() => setFilterStatus('available')}
            >
              Available ({stats.availableCount})
            </button>
            <button 
              className={`filter-btn booked ${filterStatus === 'booked' ? 'active' : ''}`}
              onClick={() => setFilterStatus('booked')}
            >
              Booked ({stats.bookedCount})
            </button>
          </div>
        </div>

        {/* Success Toast */}
        {showSuccess && (
          <div className="success-toast">
            <FontAwesomeIcon icon={faCheckCircle} className="success-icon" />
            <span>{successMessage}</span>
          </div>
        )}

        {/* Zoom Controls for Mobile */}
        <div className="zoom-controls">
          <button className="zoom-btn" onClick={handleZoomOut} title="Zoom Out">
            <span>−</span>
          </button>
          <span className="zoom-level">{Math.round(zoomLevel * 100)}%</span>
          <button className="zoom-btn" onClick={handleZoomIn} title="Zoom In">
            <span>+</span>
          </button>
          <button className="zoom-btn reset" onClick={handleResetZoom} title="Reset Zoom">
            <span>⟳</span>
          </button>
        </div>

        {/* Main Layout Display */}
        <div className="layout-container">
          
          {isCustomLayout ? (
            <div 
              className="custom-layout-container"
              style={{ 
                width: '100%',
                maxWidth: imageDimensions.width,
                height: 'auto',
                position: 'relative',
                background: '#ffffff',
                borderRadius: '12px',
                overflow: 'auto',
                border: '2px solid #34495e',
                margin: '0 auto'
              }}
            >
              <div 
                style={{
                  position: 'relative',
                  width: imageDimensions.width,
                  height: imageDimensions.height,
                  margin: '0 auto',
                  transform: `scale(${zoomLevel})`,
                  transformOrigin: 'center top',
                  transition: 'transform 0.2s ease'
                }}
              >
                {backgroundImage && showBlueprint && (
                  <img 
                    src={backgroundImage} 
                    alt="Venue Blueprint"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      opacity: imageOpacity,
                      objectFit: 'fill',
                      pointerEvents: 'none'
                    }}
                  />
                )}

                <div 
                  className="blueprint-grid"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundImage: 'linear-gradient(to right, rgba(200,200,200,0.2) 1px, transparent 1px), linear-gradient(to bottom, rgba(200,200,200,0.2) 1px, transparent 1px)',
                    backgroundSize: `${gridSize}px ${gridSize}px`,
                    pointerEvents: 'none',
                    zIndex: 2
                  }}
                />

                {fixedElements && fixedElements.length > 0 && (
                  <div>
                    {fixedElements.map(el => (
                      <div
                        key={el.id}
                        style={{
                          position: 'absolute',
                          left: el.x,
                          top: el.y,
                          width: el.width,
                          height: el.height,
                          backgroundColor: el.color || '#999',
                          border: '2px solid #333',
                          borderRadius: '8px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: `${Math.min(14, el.width / 6)}px`,
                          fontWeight: 'bold',
                          opacity: 0.9,
                          pointerEvents: 'none',
                          zIndex: 15,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                        }}
                      >
                        <span style={{ fontSize: `${Math.min(20, el.width / 4)}px`, marginRight: '5px' }}>{el.icon || '📍'}</span>
                        <span>{el.label}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="custom-stalls-container" style={{ position: 'relative', width: '100%', height: '100%' }}>
                  {getFilteredStalls().map(stall => renderCustomStall(stall))}
                </div>

                <div className="directional north">NORTH</div>
                <div className="directional south">SOUTH</div>
                <div className="directional east">EAST</div>
                <div className="directional west">WEST</div>
              </div>
            </div>
          ) : (
            <div className="predefined-layout-container" style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'center top', transition: 'transform 0.2s ease' }}>
              
              {stallPattern === 'full' && (
                <div className="layout full">
                  <div className="row top">
                    <div className="row-label">NORTH WING</div>
                    <div className="stalls-row">
                      {getFilteredStalls().filter(s => s.stallId >= 1 && s.stallId <= 12).map(stall => renderStallCard(stall))}
                    </div>
                  </div>

                  <div className="middle-section">
                    <div className="column left">
                      <div className="column-label">WEST WING</div>
                      <div className="stalls-column">
                        {getFilteredStalls().filter(s => s.stallId >= 13 && s.stallId <= 24).map(stall => renderStallCard(stall))}
                      </div>
                    </div>

                    <div className="central-area">
                      <div className="central-content">
                        <div className="compass">
                          <div className="compass-north">N</div>
                          <div className="compass-east">E</div>
                          <div className="compass-south">S</div>
                          <div className="compass-west">W</div>
                        </div>
                        <h3>EXHIBITION HALL</h3>
                        <p>Main Arena • 5000 sq.ft</p>
                      </div>
                    </div>

                    <div className="column right">
                      <div className="column-label">EAST WING</div>
                      <div className="stalls-column">
                        {getFilteredStalls().filter(s => s.stallId >= 25 && s.stallId <= 36).map(stall => renderStallCard(stall))}
                      </div>
                    </div>
                  </div>

                  <div className="row bottom">
                    <div className="row-label">SOUTH WING</div>
                    <div className="stalls-row">
                      {getFilteredStalls().filter(s => s.stallId >= 37 && s.stallId <= 50).map(stall => renderStallCard(stall))}
                    </div>
                  </div>
                </div>
              )}

              {stallPattern === 'ushape' && (
                <div className="layout ushape">
                  <div className="outer-u">
                    <div className="wing left">
                      <div className="wing-label">WEST WING</div>
                      <div className="stalls-column">
                        {getFilteredStalls().filter(s => s.stallId >= 1 && s.stallId <= 12).map(stall => renderStallCard(stall))}
                      </div>
                    </div>

                    <div className="performance-area">
                      <div className="performance-space">
                        <h3>PERFORMANCE ARENA</h3>
                        <div className="inner-u">
                          <div className="inner-left">
                            <div className="inner-label">PREMIUM LEFT</div>
                            <div className="stalls-row-inner">
                              {getFilteredStalls().filter(s => s.stallId >= 39 && s.stallId <= 44).map(stall => renderStallCard(stall))}
                            </div>
                          </div>
                          <div className="inner-center">
                            <span className="inner-center-icon">✨</span>
                            <span>VIP LOUNGE</span>
                          </div>
                          <div className="inner-right">
                            <div className="inner-label">PREMIUM RIGHT</div>
                            <div className="stalls-row-inner">
                              {getFilteredStalls().filter(s => s.stallId >= 45 && s.stallId <= 50).map(stall => renderStallCard(stall))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="wing right">
                      <div className="wing-label">EAST WING</div>
                      <div className="stalls-column">
                        {getFilteredStalls().filter(s => s.stallId >= 13 && s.stallId <= 24).map(stall => renderStallCard(stall))}
                      </div>
                    </div>
                  </div>

                  <div className="bottom-wing">
                    <div className="wing-label">SOUTH WING</div>
                    <div className="stalls-row-bottom">
                      {getFilteredStalls().filter(s => s.stallId >= 25 && s.stallId <= 38).map(stall => renderStallCard(stall))}
                    </div>
                  </div>
                </div>
              )}

              {stallPattern === 'twosides' && (
                <div className="layout twosides">
                  <div className="main-aisle">
                    <div className="sides-container">
                      <div className="side left">
                        <div className="side-label">BAZAAR - WEST</div>
                        <div className="stalls-grid-left">
                          {getFilteredStalls().filter(s => s.stallId >= 1 && s.stallId <= 25).map(stall => renderStallCard(stall))}
                        </div>
                      </div>

                      <div className="walkway">
                        <div className="walkway-content">
                          <span>MAIN WALKWAY</span>
                        </div>
                      </div>

                      <div className="side right">
                        <div className="side-label">BAZAAR - EAST</div>
                        <div className="stalls-grid-right">
                          {getFilteredStalls().filter(s => s.stallId >= 26 && s.stallId <= 50).map(stall => renderStallCard(stall))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="directional north">NORTH</div>
              <div className="directional south">SOUTH</div>
              <div className="directional east">EAST</div>
              <div className="directional west">WEST</div>
            </div>
          )}
        </div>

        {/* Booking Form Modal with Booked By Field */}
        {showBookingForm && selectedStall && (
          <div className="modal-overlay" onClick={() => setShowBookingForm(false)}>
            <div className="booking-form-modal" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={resetForm}>×</button>
              
              <div className="modal-header">
                <h2>
                  <FontAwesomeIcon icon={faStore} />
                  Book Stall #{selectedStall.stallId || selectedStall.number}
                </h2>
                <div className="stall-price">Original Price: ₹{selectedStall.price}</div>
              </div>

              <div className="whatsapp-info">
                <FontAwesomeIcon icon={faWhatsapp} className="whatsapp-icon-small" />
                <span>WhatsApp confirmation will be sent to customer's number from <strong>+91 {COMPANY_WHATSAPP}</strong></span>
              </div>

              <form onSubmit={handleBookingSubmit}>
                <div className="form-section">
                  <h3>
                    <FontAwesomeIcon icon={faUser} />
                    Customer Details
                  </h3>
                  
                  <div className="form-group">
                    <label>Full Name <span className="required">*</span></label>
                    <input
                      type="text"
                      value={customer.name}
                      onChange={(e) => setCustomer({...customer, name: e.target.value})}
                      placeholder="Enter customer name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone Number <span className="required">*</span></label>
                    <input
                      type="tel"
                      value={customer.phone}
                      onChange={(e) => setCustomer({...customer, phone: e.target.value})}
                      placeholder="10-digit mobile number"
                      maxLength="10"
                      required
                    />
                    <small className="form-hint">WhatsApp confirmation will be sent to this number</small>
                  </div>

                  <div className="form-group">
                    <label>Email (Optional)</label>
                    <input
                      type="email"
                      value={customer.email}
                      onChange={(e) => setCustomer({...customer, email: e.target.value})}
                      placeholder="customer@email.com"
                    />
                  </div>

                  <div className="form-group">
                    <label>Company (Optional)</label>
                    <input
                      type="text"
                      value={customer.company}
                      onChange={(e) => setCustomer({...customer, company: e.target.value})}
                      placeholder="Company name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Additional Notes</label>
                    <textarea
                      value={customer.notes}
                      onChange={(e) => setCustomer({...customer, notes: e.target.value})}
                      placeholder="Any special requirements..."
                      rows="3"
                    />
                  </div>

                  {/* NEW: Booked By Section */}
                  <div className="booked-by-section" style={{ 
                    marginTop: '15px', 
                    paddingTop: '15px', 
                    borderTop: '2px dashed #e0e0e0',
                    background: '#f0f4ff',
                    borderRadius: '8px',
                    padding: '15px',
                    marginBottom: '10px'
                  }}>
                    <h4 style={{ marginBottom: '12px', color: '#b8860b' }}>
                      <FontAwesomeIcon icon={faUserPlus} style={{ marginRight: '8px' }} />
                      Booking Staff Details
                    </h4>
                    <p style={{ fontSize: '0.75rem', color: '#666', marginBottom: '12px' }}>
                      Please enter the details of the staff member making this booking
                    </p>
                    
                    <div className="form-group">
                      <label>Staff Name <span className="required">*</span></label>
                      <input
                        type="text"
                        value={bookedBy.name}
                        onChange={(e) => setBookedBy({...bookedBy, name: e.target.value})}
                        placeholder="Enter your name"
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Staff Phone Number <span className="required">*</span></label>
                      <input
                        type="tel"
                        value={bookedBy.phone}
                        onChange={(e) => setBookedBy({...bookedBy, phone: e.target.value})}
                        placeholder="10-digit mobile number"
                        maxLength="10"
                        required
                      />
                      <small className="form-hint">For internal record and contact purposes</small>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3>
                    <FontAwesomeIcon icon={faMoneyBillWave} />
                    Pricing & Payment Details
                  </h3>

                  <div className="price-info-card">
                    <div className="price-row">
                      <span><FontAwesomeIcon icon={faTag} /> Original Price:</span>
                      <strong>₹{originalPrice}</strong>
                    </div>
                    
                    <div className="form-group">
                      <label><FontAwesomeIcon icon={faPercent} /> Discount Amount (₹)</label>
                      <input
                        type="number"
                        value={discount}
                        onChange={handleDiscountChange}
                        min="0"
                        max={originalPrice}
                        step="100"
                        placeholder="Enter discount amount"
                      />
                      <small className="form-hint">Enter the discount amount negotiated with customer</small>
                    </div>
                    
                    <div className="price-row final-price">
                      <span><FontAwesomeIcon icon={faMoneyBillWave} /> Final Price:</span>
                      <strong className="final-amount">₹{finalPrice}</strong>
                    </div>
                  </div>

                  <div className="form-group">
                    <label><FontAwesomeIcon icon={faCreditCard} /> Payment Method</label>
                    <select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    >
                      <option value="CASH">💵 Cash</option>
                      <option value="ONLINE">💳 Online Payment</option>
                      <option value="UPI">📱 UPI</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label><FontAwesomeIcon icon={faCheckCircle} /> Payment Status</label>
                    <select
                      value={paymentStatus}
                      onChange={(e) => setPaymentStatus(e.target.value)}
                    >
                      <option value="PAID">✅ Paid</option>
                      <option value="PENDING">⏳ Pending</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label><FontAwesomeIcon icon={faImage} /> Payment Receipt (Optional)</label>
                    <div className="receipt-upload-area">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handlePaymentReceiptUpload}
                        accept="image/jpeg,image/png,image/jpg,image/webp"
                        style={{ display: 'none' }}
                      />
                      {!paymentReceiptPreview ? (
                        <button 
                          type="button" 
                          className="upload-btn"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <FontAwesomeIcon icon={faUpload} />
                          Upload Receipt Image
                        </button>
                      ) : (
                        <div className="receipt-preview">
                          <img src={paymentReceiptPreview} alt="Payment Receipt" />
                          <button 
                            type="button" 
                            className="remove-receipt-btn"
                            onClick={removePaymentReceipt}
                          >
                            <FontAwesomeIcon icon={faTrash} /> Remove
                          </button>
                        </div>
                      )}
                      <small className="form-hint">Upload JPG, PNG, or WEBP (Max 5MB)</small>
                    </div>
                  </div>

                  <div className="payment-summary">
                    <div className="summary-row">
                      <span>Original Price:</span>
                      <span>₹{originalPrice}</span>
                    </div>
                    {discount > 0 && (
                      <div className="summary-row discount">
                        <span>Discount:</span>
                        <span>- ₹{discount}</span>
                      </div>
                    )}
                    <div className="summary-row total">
                      <span>Final Amount:</span>
                      <span className="final-amount">₹{finalPrice}</span>
                    </div>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" className="cancel-btn" onClick={resetForm}>
                    Cancel
                  </button>
                  <button type="submit" className="submit-btn" disabled={isSaving || whatsappSending}>
                    {isSaving || whatsappSending ? (
                      <>
                        <span className="spinner"></span>
                        {whatsappSending ? 'Opening WhatsApp...' : 'Booking...'}
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faWhatsapp} />
                        Confirm & Send WhatsApp
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Floating Buttons */}
      <div className={`floating-buttons ${showFloatingButtons ? 'show' : ''}`}>
        <button className="floating-btn whatsapp" onClick={handleGlobalWhatsAppClick}>
          <i className="bi bi-whatsapp"></i>
        </button>
        <button className="floating-btn top" onClick={handleBackToTop}>
          <i className="bi bi-arrow-up"></i>
        </button>
      </div>
      <div className={`back-to-top ${showBackToTop ? 'show' : ''}`} onClick={handleBackToTop}>
        <i className="bi bi-arrow-up"></i>
      </div>

      <Footer />
    </>
  );
}

export default AdminStallBooking;